/**
 * MarkJointTripsModal — joint-trip picker with smart duplicate detection.
 *
 * Data source: GET /api/partner/shared-trips/candidates
 * Commit endpoints (per row type):
 *   - Pair row              → POST /shared-trips/link-pair
 *   - Pair "Not the same"   → POST /shared-trips/dismiss-pair
 *   - Mine (own trip) tick  → POST /shared-trips/bulk-confirm
 *   - Mine untick           → POST /shared-trips/toggle  (joint:false)
 *   - Partner-only tick     → user picks one of two via PartnerTripActionDialog:
 *      a) "Add to my list" → POST /shared-trips/add-partner-trip   (copies + links)
 *      b) "Just shared"    → POST /shared-trips/bulk-confirm        (no copy)
 *
 * Design rules (per product spec — locked decisions 1a, 2a, 3a, 4c, 5a, 7a):
 *   - Pair-detection: same country + ±30 days. Strong (≤7d) vs weak.
 *   - Always show a "Not the same trip" escape hatch on every pair row.
 *   - For partner-only ticks we ALWAYS ASK — no silent default.
 *   - Cover photo is the only thing we copy when "Add to my list".
 *   - Planned + completed trips both supported.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Heart, MapPin, Calendar, Loader2, CheckCheck, X, Sparkles, ListPlus, Share2,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short" });
}

// ── Sub-modal: ask how to handle a partner-only ticked trip ────────────
function PartnerTripActionDialog({ open, candidate, partnerName, onChoose, onCancel }) {
  if (!candidate) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="partner-trip-action-dialog">
        <DialogHeader>
          <DialogTitle>You haven't logged "{candidate.title}" yet</DialogTitle>
          <DialogDescription>
            {partnerName} logged this. How would you like to handle it on your side?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          <button
            type="button"
            onClick={() => onChoose("add")}
            className="w-full text-left p-3 rounded-lg border border-pink-500/40 bg-pink-500/5 hover:bg-pink-500/10 transition-colors"
            data-testid="partner-action-add"
          >
            <div className="flex items-center gap-2 font-medium text-sm">
              <ListPlus className="w-4 h-4 text-pink-500" /> Add to my bucket list too
            </div>
            <p className="text-[12px] text-muted-foreground mt-1">
              Creates a copy on your dashboard with the cover photo only. Counts in your stats.
              Photos &amp; notes stay private to {partnerName}.
            </p>
          </button>
          <button
            type="button"
            onClick={() => onChoose("shared_only")}
            className="w-full text-left p-3 rounded-lg border border-border hover:border-border/80 bg-card hover:bg-muted/30 transition-colors"
            data-testid="partner-action-shared-only"
          >
            <div className="flex items-center gap-2 font-medium text-sm">
              <Share2 className="w-4 h-4 text-muted-foreground" /> Just mark as shared
            </div>
            <p className="text-[12px] text-muted-foreground mt-1">
              Only shows in the Duo map &amp; Adventure Combined. Won't appear on your bucket list.
            </p>
          </button>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarkJointTripsModal({ open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [mine, setMine] = useState([]);
  const [partnerTrips, setPartnerTrips] = useState([]);
  const [partner, setPartner] = useState(null);
  const [me, setMe] = useState(null);

  // Selection state
  const [selectedPairs, setSelectedPairs] = useState(() => new Set());      // pair index keys "mine_id::partner_id"
  const [selectedMine, setSelectedMine] = useState(() => new Set());         // own item ids
  // Partner ticks: map<partner_item_id, "add" | "shared_only" | undefined>
  const [partnerActions, setPartnerActions] = useState(() => new Map());
  const [showPlanned, setShowPlanned] = useState(false);

  // Action chooser modal state
  const [pendingPartnerTrip, setPendingPartnerTrip] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner/shared-trips/candidates`);
      setPairs(data.pairs || []);
      setMine(data.mine || []);
      setPartnerTrips(data.partner_trips || []);
      setPartner(data.partner || null);
      setMe(data.me || null);
      // Pre-select already-linked own trips so toggling off is one click
      const linkedOwn = new Set((data.mine || []).filter((m) => m.already_linked).map((m) => m.id));
      setSelectedMine(linkedOwn);
      // Partner items already linked are pre-set as "shared_only" (we don't
      // know which ones were originally "add" copies — those would already be
      // in `mine` as separate items)
      const partnerActs = new Map();
      (data.partner_trips || []).forEach((p) => {
        if (p.already_linked) partnerActs.set(p.id, "shared_only");
      });
      setPartnerActions(partnerActs);
      setSelectedPairs(new Set());
    } catch (e) {
      toast.error("Could not load trips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const partnerName = partner?.name || "your partner";

  const togglePair = (pair) => {
    const key = `${pair.mine.id}::${pair.partner.id}`;
    setSelectedPairs((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const toggleMine = (id) => {
    setSelectedMine((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const togglePartner = (item) => {
    const current = partnerActions.get(item.id);
    if (current) {
      // Untick — clear action
      setPartnerActions((prev) => {
        const n = new Map(prev);
        n.delete(item.id);
        return n;
      });
      return;
    }
    // First tick — open the chooser modal
    setPendingPartnerTrip(item);
  };

  const dismissPair = async (pair) => {
    try {
      await axios.post(`${API}/partner/shared-trips/dismiss-pair`, {
        item_a_id: pair.mine.id, item_b_id: pair.partner.id,
      });
      // Remove the pair locally and put both items back as separate rows
      setPairs((p) => p.filter((x) => !(x.mine.id === pair.mine.id && x.partner.id === pair.partner.id)));
      setMine((m) => [...m, pair.mine].sort((a, b) => (a.completed === b.completed ? 0 : a.completed ? -1 : 1)));
      setPartnerTrips((p) => [...p, pair.partner].sort((a, b) => (a.completed === b.completed ? 0 : a.completed ? -1 : 1)));
      toast.success("Marked as separate trips");
    } catch (e) {
      toast.error("Could not split that pair");
    }
  };

  const totalSelected =
    selectedPairs.size + selectedMine.size + partnerActions.size;

  const save = async () => {
    setSaving(true);
    try {
      // 1. PAIRS — explicit link-pair calls
      for (const key of selectedPairs) {
        const [aId, bId] = key.split("::");
        await axios.post(`${API}/partner/shared-trips/link-pair`, {
          item_a_id: aId, item_b_id: bId,
        });
      }
      // 2. MINE — bulk-confirm new ticks; toggle-off un-ticked
      const prevLinkedMine = new Set(mine.filter((m) => m.already_linked).map((m) => m.id));
      const mineToAdd = [...selectedMine].filter((id) => !prevLinkedMine.has(id));
      const mineToRemove = [...prevLinkedMine].filter((id) => !selectedMine.has(id));
      if (mineToAdd.length > 0) {
        await axios.post(`${API}/partner/shared-trips/bulk-confirm`, { item_ids: mineToAdd });
      }
      for (const id of mineToRemove) {
        await axios.post(`${API}/partner/shared-trips/toggle`, { item_id: id, joint: false });
      }
      // 3. PARTNER actions
      const prevLinkedPartner = new Map(
        partnerTrips.filter((p) => p.already_linked).map((p) => [p.id, "shared_only"]),
      );
      const partnerToRemove = [...prevLinkedPartner.keys()].filter((id) => !partnerActions.has(id));
      for (const [partnerItemId, action] of partnerActions) {
        if (prevLinkedPartner.has(partnerItemId)) continue; // already linked — no-op
        if (action === "add") {
          await axios.post(`${API}/partner/shared-trips/add-partner-trip`, {
            partner_item_id: partnerItemId,
          });
        } else {
          await axios.post(`${API}/partner/shared-trips/bulk-confirm`, {
            item_ids: [partnerItemId],
          });
        }
      }
      for (const id of partnerToRemove) {
        await axios.post(`${API}/partner/shared-trips/toggle`, { item_id: id, joint: false });
      }
      toast.success(`Updated — ${totalSelected} shared trip${totalSelected === 1 ? "" : "s"}`);
      onSaved && onSaved({ selectedCount: totalSelected });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Row renderers ───────────────────────────────────────────────────
  const renderPairRow = (pair, idx) => {
    const key = `${pair.mine.id}::${pair.partner.id}`;
    const picked = selectedPairs.has(key);
    const strong = pair.strength === "strong";
    return (
      <div
        key={key}
        className={`p-3 rounded-lg border-2 transition-colors ${
          picked ? "border-pink-500/60 bg-pink-500/5" : "border-violet-500/30 bg-violet-500/5"
        }`}
        data-testid={`joint-pair-${idx}`}
      >
        <div className="flex items-start gap-3">
          <Checkbox
            checked={picked}
            onCheckedChange={() => togglePair(pair)}
            className="mt-0.5"
            aria-label={`Link pair ${pair.mine.title} and ${pair.partner.title}`}
            data-testid={`joint-pair-checkbox-${idx}`}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <Sparkles className={`w-3.5 h-3.5 ${strong ? "text-pink-500" : "text-amber-400"}`} />
              <span className={`text-[11px] font-medium uppercase tracking-wider ${
                strong ? "text-pink-500" : "text-amber-400"
              }`}>
                {strong ? "Same trip — link both" : "Possibly the same trip — review"}
              </span>
              {pair.mine.country_code && (
                <span className="text-[11px] text-muted-foreground inline-flex items-center">
                  <MapPin className="w-3 h-3 mr-0.5" />{pair.mine.country_code}
                </span>
              )}
            </div>
            <div className="text-sm space-y-0.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-pink-500/80 text-[11px] font-medium uppercase">You:</span>
                <span className="font-medium truncate">{pair.mine.title || "Untitled"}</span>
                {(pair.mine.visited_date || pair.mine.target_date) && (
                  <span className="text-[11px] text-muted-foreground">
                    · {formatDate(pair.mine.visited_date || pair.mine.target_date)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-violet-400/80 text-[11px] font-medium uppercase">{partnerName}:</span>
                <span className="font-medium truncate">{pair.partner.title || "Untitled"}</span>
                {(pair.partner.visited_date || pair.partner.target_date) && (
                  <span className="text-[11px] text-muted-foreground">
                    · {formatDate(pair.partner.visited_date || pair.partner.target_date)}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => dismissPair(pair)}
              className="mt-2 text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              data-testid={`joint-pair-dismiss-${idx}`}
            >
              Not the same trip — keep separate
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMineRow = (c) => {
    const picked = selectedMine.has(c.id);
    return (
      <div
        key={c.id}
        role="button"
        tabIndex={0}
        onClick={() => toggleMine(c.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleMine(c.id); }
        }}
        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer ${
          picked ? "border-pink-500/60 bg-pink-500/5" : "border-border hover:border-border/80 bg-card"
        }`}
        data-testid={`joint-mine-${c.id}`}
      >
        <Checkbox checked={picked} onCheckedChange={() => toggleMine(c.id)} onClick={(e) => e.stopPropagation()} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{c.title || "Untitled trip"}</span>
            {c.completed && <Badge variant="outline" className="text-[10px]">Completed</Badge>}
            {!c.completed && <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">Planned</Badge>}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
            {c.country_code && <span className="inline-flex items-center"><MapPin className="w-3 h-3 mr-0.5" />{c.country_code}</span>}
            {(c.visited_date || c.target_date) && (
              <span className="inline-flex items-center"><Calendar className="w-3 h-3 mr-0.5" />{formatDate(c.visited_date || c.target_date)}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPartnerRow = (c) => {
    const action = partnerActions.get(c.id);
    const picked = !!action;
    return (
      <div
        key={c.id}
        role="button"
        tabIndex={0}
        onClick={() => togglePartner(c)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); togglePartner(c); }
        }}
        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer ${
          picked ? "border-pink-500/60 bg-pink-500/5" : "border-border hover:border-border/80 bg-card"
        }`}
        data-testid={`joint-partner-${c.id}`}
      >
        <Checkbox checked={picked} onCheckedChange={() => togglePartner(c)} onClick={(e) => e.stopPropagation()} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{c.title || "Untitled trip"}</span>
            {c.completed && <Badge variant="outline" className="text-[10px]">Completed</Badge>}
            {!c.completed && <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">Planned</Badge>}
            {action === "add" && <Badge className="text-[10px] bg-pink-500/20 text-pink-300 border-pink-500/30">Adding to my list</Badge>}
            {action === "shared_only" && <Badge className="text-[10px] bg-muted text-muted-foreground">Shared only</Badge>}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
            {c.country_code && <span className="inline-flex items-center"><MapPin className="w-3 h-3 mr-0.5" />{c.country_code}</span>}
            {(c.visited_date || c.target_date) && (
              <span className="inline-flex items-center"><Calendar className="w-3 h-3 mr-0.5" />{formatDate(c.visited_date || c.target_date)}</span>
            )}
            <span>· {partnerName}</span>
          </div>
        </div>
      </div>
    );
  };

  const completedMine = useMemo(() => mine.filter((c) => c.completed), [mine]);
  const plannedMine = useMemo(() => mine.filter((c) => !c.completed), [mine]);
  const completedPartner = useMemo(() => partnerTrips.filter((c) => c.completed), [partnerTrips]);
  const plannedPartner = useMemo(() => partnerTrips.filter((c) => !c.completed), [partnerTrips]);
  const plannedCount = plannedMine.length + plannedPartner.length;

  const empty = !loading && pairs.length === 0 && mine.length === 0 && partnerTrips.length === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="mark-joint-trips-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-500" /> Which trips did you go on together?
            </DialogTitle>
            <DialogDescription>
              Tick the trips you experienced with {partnerName}. These show up in your Duo map
              and "Visited Together" stats. Photos &amp; notes stay private.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {loading && (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            )}
            {empty && (
              <p className="text-sm text-muted-foreground py-6 text-center">
                Neither of you has any trips yet — add some first and you'll be able to mark them as shared here.
              </p>
            )}

            {!loading && pairs.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-pink-500" /> Likely the same trip
                </p>
                <div className="space-y-2">{pairs.map(renderPairRow)}</div>
              </div>
            )}

            {!loading && completedMine.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Your completed trips</p>
                <div className="space-y-2">{completedMine.map(renderMineRow)}</div>
              </div>
            )}

            {!loading && completedPartner.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{partnerName}'s completed trips</p>
                <div className="space-y-2">{completedPartner.map(renderPartnerRow)}</div>
              </div>
            )}

            {!loading && plannedCount > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowPlanned((v) => !v)}
                  className="text-xs text-primary hover:underline"
                  data-testid="toggle-planned"
                >
                  {showPlanned ? "Hide" : "Show"} planned / upcoming trips ({plannedCount})
                </button>
                {showPlanned && (
                  <div className="mt-2 space-y-2">
                    {plannedMine.map(renderMineRow)}
                    {plannedPartner.map(renderPartnerRow)}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 flex-shrink-0">
            <div className="text-xs text-muted-foreground mr-auto">{totalSelected} selected</div>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving || loading} data-testid="save-joint-trips">
              <CheckCheck className="w-4 h-4 mr-1" />
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PartnerTripActionDialog
        open={!!pendingPartnerTrip}
        candidate={pendingPartnerTrip}
        partnerName={partnerName}
        onChoose={(action) => {
          setPartnerActions((prev) => {
            const n = new Map(prev);
            n.set(pendingPartnerTrip.id, action);
            return n;
          });
          setPendingPartnerTrip(null);
        }}
        onCancel={() => setPendingPartnerTrip(null)}
      />
    </>
  );
}
